import { useCallback } from 'react';
import { NavigateOptions, To, useNavigate, useParams } from 'react-router-dom';

import { i18n } from '../i18n';

import { buildLocalePath, DEFAULT_LOCALE, isSupportedLocale } from './localeConfig';

/**
 * Drop-in replacement for useNavigate that automatically prepends /:lang prefix.
 * Handles relative paths (no prefix), absolute paths (adds prefix),
 * and numeric navigation like navigate(-1).
 */
export const useLocaleNavigate = () => {
  const navigate = useNavigate();
  const params = useParams<{ lang?: string }>();

  const getCurrentLocale = useCallback((): string => {
    // Priority: URL param > i18n language > default
    if (params.lang && isSupportedLocale(params.lang)) {
      return params.lang;
    }
    return i18n.language || DEFAULT_LOCALE;
  }, [params.lang]);

  return useCallback(
    (to: To | number, options?: NavigateOptions) => {
      // Numeric navigation (e.g. navigate(-1)) - pass through
      if (typeof to === 'number') {
        return navigate(to);
      }

      const locale = getCurrentLocale();

      // String path
      if (typeof to === 'string') {
        return navigate(buildLocalePath(to, locale), options);
      }

      // Object path { pathname, search, hash }
      if (typeof to === 'object' && to.pathname) {
        return navigate(
          { ...to, pathname: buildLocalePath(to.pathname, locale) },
          options,
        );
      }

      // Fallback - pass through unchanged
      return navigate(to, options);
    },
    [navigate, getCurrentLocale],
  );
};
